import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import Slider from 'react-slick'
import { FaChevronLeft, FaChevronRight, FaPlay, FaBookOpen, FaVideo, FaQrcode, FaQuoteLeft, FaQuoteRight, FaHandHoldingHeart } from 'react-icons/fa'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import './wallOfPromise.css'
import LoveSmarterBook from '../book/LoveSmarterBook'

const NextArrow = ({ onClick }) => (
  <div
    className="custom-slick-arrow custom-slick-next"
    onClick={onClick}
  >
    <FaChevronRight size={18} color="#ffffff" />
  </div>
);

const PrevArrow = ({ onClick }) => (
  <div
    className="custom-slick-arrow custom-slick-prev"
    onClick={onClick}
  >
    <FaChevronLeft size={18} color="#ffffff" />
  </div>
);

const WallOfPromise = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  const handleVideoToggle = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsVideoPlaying(true);
      } else {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      }
    }
  };

  useEffect(() => {
    if (location.hash === '#featured-video' || location.hash === '#video') {
      setTimeout(() => {
        const element = document.getElementById('featured-video');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [location]);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 700,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const carouselImages = [
    { id: 1, src: '/image11.png', alt: 'Wall of Promise 1' },
    { id: 2, src: '/image12.png', alt: 'Wall of Promise 2' },
    { id: 3, src: '/image13.png', alt: 'Wall of Promise 3' },
    { id: 4, src: '/image14.png', alt: 'Wall of Promise 4' },
  ];

  return (
    <div className="wall-of-promise-page">
      <Helmet>
        <title>The Wall of Promise | Fund Life</title>
        <meta
          name="description"
          content="The Wall of Promise gives sponsors an opportunity to do more than give—personally encourage HBCU students you are investing in."
        />
      </Helmet>

      {/* First Section: Hero with Background & Book */}
      <div className="wall-of-promise-hero">
        <img
          src="/BG.jpg"
          alt="Wall of Promise Background"
          className="wall-of-promise-bg"
          loading="lazy"
          decoding="async"
        />
        <div className="wall-of-promise-content">
          <LoveSmarterBook />
        </div>
      </div>

      {/* One Book Challenge Section */}
      <section className="one-book-challenge-section">
        <div className="challenge-container text-center">
          <h3 className="challenge-tagline">
            ONE BOOK. ONE STUDENT. ONE FUTURE.
          </h3>
          <h2 className="challenge-headline">
            GENERATIONS CHANGED
          </h2>
          <div className="challenge-divider"></div>
          <p className="challenge-description">
            “Help us place this book into the hands of every HBCU student, beginning with Texas Southern University and Prairie View A&M University.”
          </p>
          <div className="challenge-cta-wrapper">
            <button
              className="challenge-cta-button"
              onClick={() => navigate('/checkout')}
            >
              TAKE THE ONE BOOK CHALLENGE ➔
            </button>
            <span className="challenge-subtext">
              $40 SPONSORS TWO STUDENTS
            </span>
          </div>
        </div>
      </section>

      {/* Second Section: Wall of Promise */}
      <section className="third-section" style={{ backgroundImage: "url('/bg3.png')" }}>
        <div className="third-section-container">
          {/* Main Top Header */}
          <div className="wall-main-hero-header text-center">
            <h2 className="wall-hero-title">THE WALL OF PROMISE</h2>
            <p className="wall-hero-intro">
              The Wall of Promise gives sponsors an opportunity to do more than give—you can personally encourage the HBCU students you’re investing in.
            </p>
          </div>

          {/* 3 Horizontal Feature Cards */}
          <div className="wall-perks-three-grid">
            <div className="wall-perk-card">
              <div className="perk-icon-circle">
                <FaBookOpen />
              </div>
              <div className="perk-card-content">
                <h4>3+ Book Sponsorship</h4>
                <p>With a minimum of 3 book sponsorship here’s where your name and an encouraging message from you will be displayed.</p>
              </div>
            </div>

            <div className="wall-perk-card">
              <div className="perk-icon-circle">
                <FaVideo />
              </div>
              <div className="perk-card-content">
                <h4>50+ Book Sponsorship</h4>
                <p>With a 50 book sponsorship you can post a 45-second video message sharing encouragement, wisdom, or inspiration.</p>
              </div>
            </div>

            <div className="wall-perk-card">
              <div className="perk-icon-circle">
                <FaQrcode />
              </div>
              <div className="perk-card-content">
                <h4>Direct QR Connection</h4>
                <p>When a student scans the QR code inside a book you sponsored, they are taken directly to your personal message or video on the Wall of Promise—connecting your gift with the student who received it.</p>
              </div>
            </div>
          </div>

          {/* Golden Quote Strip */}
          <div className="wall-quote-strip">
            <p className="wall-quote-lead">
              The wall transforms personal sponsorship into a personal connection between those who give and the students they support. Your sponsorship sends a powerful message:
            </p>
            <div className="wall-quote-highlight">
              <FaQuoteLeft className="quote-icon-sm" />
              <span>I believe in you. I want to see you succeed.</span>
              <FaQuoteRight className="quote-icon-sm quote-icon-right" />
            </div>
            <div className="wall-quote-tagline">
              <span>SPONSOR</span> • <span>ENCOURAGE</span> • <span>INSPIRE</span>
            </div>
          </div>

          {/* Side-by-Side Media Showcases */}
          <div className="wall-media-two-col-grid">
            {/* Left Column: Carousel Showcase */}
            <div className="wall-showcase-col">
              <div className="showcase-col-header text-center mb-3">
                <span className="col-sub-pill">SAMPLE MESSAGE</span>
                <h3 className="showcase-col-title">Messages of Promise</h3>
                <p className="showcase-col-subtitle">Explore notes of encouragement left by sponsors</p>
              </div>

              <div className="carousel-slider-container">
                <Slider {...sliderSettings} className="wall-of-promise-slider">
                  {carouselImages.map((img) => (
                    <div key={img.id} className="slider-item-padding">
                      <div className="carousel-img-wrapper">
                        <img
                          className="carousel-img"
                          src={img.src}
                          alt={img.alt}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>
            </div>

            {/* Right Column: Featured Video Showcase */}
            <div className="wall-showcase-col" id="featured-video">
              <div className="showcase-col-header text-center mb-3">
                <span className="col-sub-pill">SAMPLE VIDEO</span>
                <h3 className="showcase-col-title">Witness the Impact</h3>
                <p className="showcase-col-subtitle">See how Love Smarter, Not Harder transforms lives</p>
              </div>

              <div className="wall-of-promise-video-container">
                <div className="video-glow-effect"></div>
                <div className="video-responsive-wrapper">
                  {!isVideoPlaying && (
                    <div
                      className="video-custom-play-overlay"
                      onClick={handleVideoToggle}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        handleVideoToggle();
                      }}
                    >
                      <div className="custom-play-btn">
                        <FaPlay className="play-icon" />
                      </div>
                      <span className="play-text">Sample Message of Promise</span>
                    </div>
                  )}
                  <video
                    ref={videoRef}
                    src="/wall_of_promise_video.mp4"
                    controls={isVideoPlaying}
                    playsInline
                    preload="metadata"
                    onClick={handleVideoToggle}
                    onPlay={() => setIsVideoPlaying(true)}
                    onPause={() => setIsVideoPlaying(false)}
                    className="native-video-player"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Third Section: Founder Quote Section (Mike Goss) */}
      <section className="founder-section">
        <div className="founder-container">
          <div className="founder-grid">
            {/* Left Column: Founder Photo Frame */}
            <div className="founder-image-wrapper">
              <div className="founder-image-frame">
                <img
                  src="/bg2.png"
                  alt="Mike Goss, Founder"
                  className="founder-img"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            {/* Right Column: Founder Quote Content */}
            <div className="founder-content">
              <div className="quote-mark">“</div>
              <h2 className="founder-greeting">Friend,</h2>

              <div className="founder-body">
                <p>
                  I call you friend because that is what you become when you choose to stand with us in placing <em>Love Smarter, Not Harder</em> into the hands of college students.
                </p>
                <p>
                  You are doing more than providing a book. You are helping give a young person relationship wisdom that could influence whom they love, whom they marry, the family they build, and ultimately the direction of their life.
                </p>
                <p>
                  Colleges do an extraordinary job preparing students to make a living. Together, we can help prepare them for life.
                </p>
                <p>
                  That is why I believe this is the one book every college student should have the opportunity to read.
                </p>
                <p className="highlight-text">
                  Thank you for believing in our students, investing in their future, and standing with us.
                </p>
              </div>

              <div className="founder-signature">
                Mike Goss, Founder
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className='fourth-section'>
        <div className='container text-center' style={{ maxWidth: '960px' }}>
          <div className="fourth-section-header">
            <h2 className="fourth-section-title">
              Choose Who You Would Like to Support
            </h2>
            <p className="fourth-section-desc">
              Help us provide books to students at HBCUs and make a lasting impact.
            </p>
          </div>
          <div className="school-cards-grid">
            {/* TSU Card */}
            <div
              className="school-feature-card tsu-feature-card"
              onClick={() => navigate('/checkout?school=tsu')}
            >
              <div className="school-card-banner">
                <img
                  src="/TSU.png"
                  alt="Texas Southern University"
                  className="school-banner-img"
                  loading="lazy"
                  decoding="async"
                />
                <div className="school-banner-gradient tsu-gradient"></div>
              </div>
              <div className="school-card-body">
                <h3 className="school-card-title tsu-title">
                  Sponsor Students at TSU
                </h3>
                <p className="school-card-subtitle">
                  Support deserving students at Texas Southern University.
                </p>
                <button
                  type="button"
                  className="school-card-btn tsu-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/checkout?school=tsu');
                  }}
                >
                  Sponsor TSU Students ➔
                </button>
              </div>
            </div>

            {/* PVAMU Card */}
            <div
              className="school-feature-card pvamu-feature-card"
              onClick={() => navigate('/checkout?school=pvamu')}
            >
              <div className="school-card-banner">
                <img
                  src="/PVAMU.png"
                  alt="Prairie View A&M University"
                  className="school-banner-img"
                  loading="lazy"
                  decoding="async"
                />
                <div className="school-banner-gradient pvamu-gradient"></div>
                {/* <div className="pvamu-header-brand-overlay">
                  <div className="pvamu-brand-badge-box">PVAMU</div>
                  <div className="pvamu-brand-text">
                    Prairie View A&M<br />University
                  </div>
                </div> */}
              </div>
              <div className="school-card-body">
                <h3 className="school-card-title pvamu-title">
                  Sponsor Students at PVAMU
                </h3>
                <p className="school-card-subtitle">
                  Support deserving students at Prairie View A&M University.
                </p>
                <button
                  type="button"
                  className="school-card-btn pvamu-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/checkout?school=pvamu');
                  }}
                >
                  Sponsor PVAMU Students ➔
                </button>
              </div>
            </div>
          </div>

          {/* Third Option: General HBCU Population Card */}
          <div
            className="general-hbcu-card"
            onClick={() => navigate('/checkout?school=hbcu')}
          >
            <div className="general-hbcu-left">
              <div className="general-hbcu-icon-circle">
                <FaHandHoldingHeart className="general-hbcu-heart-icon" />
              </div>
              <div className="general-hbcu-text">
                <h3 className="general-hbcu-title">Support the General HBCU Community</h3>
                <p className="general-hbcu-subtitle">Help us reach more students across all HBCUs.</p>
              </div>
            </div>
            <button
              type="button"
              className="general-hbcu-btn"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/checkout?school=hbcu');
              }}
            >
              Sponsor Fallback Books 2026 ➔
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default WallOfPromise